<template>
  <div class="">
    <Header></Header>

    <main class="u-main">
      <Navigation></Navigation>

      <div class="u-content">
        <div class="u-body min-h-700">
          <h1 class="h2 mb-2">Booking Detail</h1>

          <!-- Breadcrumb -->
          <nav aria-label="breadcrumb">
            <ol class="breadcrumb">
              <li class="breadcrumb-item">
                <router-link :to="{ name: 'dashboard' }">Dashboard</router-link>
              </li>
              <li class="breadcrumb-item">
                <router-link :to="{ name: 'bookings' }">Bookings</router-link>
              </li>
              <li class="breadcrumb-item active" aria-current="page">
                {{ row.confirmation_number || "..." }}
              </li>
            </ol>
          </nav>
          <!-- End Breadcrumb -->

          <div v-if="pgLoading" class="row h-100">
            <div class="container text-center">
              <div class="spinner-grow mt-5" role="status">
                <span class="sr-only">Loading...</span>
              </div>
            </div>
          </div>

          <div v-if="!pgLoading" class="row">
            <!-- Left Column -->
            <div class="col-md-8 mb-5">
              <!-- Booking Info -->
              <div class="card">
                <div class="card-header">
                  <h2 class="h4 card-header-title">Booking Info</h2>
                </div>
                <div class="card-body">
                  <table class="table table-sm mb-0">
                    <tbody>
                      <tr>
                        <th style="width:35%">Confirmation #</th>
                        <td>
                          <strong>{{ row.confirmation_number }}</strong>
                        </td>
                      </tr>
                      <tr>
                        <th>Tenant</th>
                        <td>{{ row.tenant_name }}</td>
                      </tr>
                      <tr>
                        <th>Package</th>
                        <td>{{ row.package_title }}</td>
                      </tr>
                      <tr>
                        <th>Travel Date</th>
                        <td>
                          <span
                            v-if="row.travel_month"
                            class="font-weight-semi-bold"
                            >{{ travelMonthLabel(row) }}</span
                          >
                          <span v-else class="text-muted">–</span>
                        </td>
                      </tr>
                      <tr>
                        <th>Adults</th>
                        <td>{{ row.adult_count }}</td>
                      </tr>
                      <tr>
                        <th>Children</th>
                        <td>
                          <span v-if="row.children && row.children.length">{{
                            childrenSummary
                          }}</span>
                          <span v-else class="text-muted">–</span>
                        </td>
                      </tr>
                      <tr>
                        <th>Payment Ref</th>
                        <td>{{ row.payment_reference || "–" }}</td>
                      </tr>
                      <tr>
                        <th>ERP Token</th>
                        <td>{{ row.erp_request_token || "–" }}</td>
                      </tr>
                      <tr>
                        <th>Created</th>
                        <td>{{ row.created_at }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <!-- End Booking Info -->

              <!-- Traveler Info (from user) -->
              <div class="card mt-4">
                <div class="card-header">
                  <h2 class="h4 card-header-title">Traveler</h2>
                </div>
                <div class="card-body">
                  <table class="table table-sm mb-0">
                    <tbody>
                      <tr>
                        <th style="width:35%">Name</th>
                        <td>{{ row.user_name || "–" }}</td>
                      </tr>
                      <tr>
                        <th>Email</th>
                        <td>{{ row.user_email || "–" }}</td>
                      </tr>
                      <tr>
                        <th>IP Address</th>
                        <td class="text-break">
                          <span v-if="row.ip_address">
                            {{ row.ip_address }}
                            <a
                              href="javascript:;"
                              class="link-muted ml-1 ti-files f12"
                              v-tooltip="'Copy IP'"
                              v-clipboard:copy="row.ip_address"
                              v-clipboard:success="onCopy"
                            >
                            </a>
                          </span>
                          <span v-else class="text-muted">–</span>
                        </td>
                      </tr>
                      <tr>
                        <th>Location</th>
                        <td>
                          <span v-if="locationLabel">{{ locationLabel }}</span>
                          <span v-else-if="row.ip_address" class="text-muted"
                            >Location unavailable</span
                          >
                          <span v-else class="text-muted">–</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <!-- End Traveler Info -->

              <!-- Room Items -->
              <div class="card mt-4" v-if="row.items && row.items.length">
                <div class="card-header">
                  <h2 class="h4 card-header-title">Rooms</h2>
                </div>
                <div class="card-body p-0">
                  <table class="table table-sm mb-0">
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th class="text-center">Travelers</th>
                        <th class="text-right">Unit Price</th>
                        <th class="text-right">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(item, i) in row.items" :key="'item_' + i">
                        <td class="text-capitalize">{{ item.room_type }}</td>
                        <td class="text-center">{{ item.travelers }}</td>
                        <td class="text-right">
                          {{ Number(item.unit_price).toFixed(2) }}
                        </td>
                        <td class="text-right">
                          {{ Number(item.subtotal).toFixed(2) }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <!-- End Room Items -->

              <!-- Add-ons -->
              <div class="card mt-4" v-if="row.add_ons && row.add_ons.length">
                <div class="card-header">
                  <h2 class="h4 card-header-title">Instant Booking Extras</h2>
                </div>
                <div class="card-body p-0">
                  <table class="table table-sm mb-0">
                    <thead>
                      <tr>
                        <th>Extra</th>
                        <th class="text-center">Travelers</th>
                        <th class="text-right">Unit Price</th>
                        <th class="text-right">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(addon, i) in row.add_ons" :key="'addon_' + i">
                        <td>{{ addon.title }}</td>
                        <td class="text-center">{{ addon.travelers }}</td>
                        <td class="text-right">
                          {{ Number(addon.unit_price).toFixed(2) }}
                        </td>
                        <td class="text-right">
                          {{ Number(addon.subtotal).toFixed(2) }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <!-- End Add-ons -->
            </div>
            <!-- End Left Column -->

            <!-- Right Sidebar -->
            <div class="col-md-4 mb-5">
              <!-- Status Card -->
              <div class="card">
                <div class="card-header">
                  <h2 class="h4 card-header-title">Status</h2>
                </div>
                <div class="card-body">
                  <div class="mb-3">
                    <span
                      :class="statusBadge(row.status)"
                      style="font-size:1rem; padding:.4em .8em"
                    >
                      {{ row.status }}
                    </span>
                  </div>
                  <div class="form-group">
                    <label>Change Status</label>
                    <select class="form-control" v-model="newStatus">
                      <option value="pending">Pending</option>
                      <option value="paid">Paid</option>
                      <option value="cancelled">Cancelled</option>
                      <option value="refunded">Refunded</option>
                    </select>
                  </div>
                  <button
                    class="btn btn-primary btn-block"
                    :disabled="btnLoading"
                    @click="saveStatus"
                  >
                    <span v-if="btnLoading">
                      <span
                        class="spinner-grow spinner-grow-sm mr-1"
                        role="status"
                        aria-hidden="true"
                      ></span
                      >Saving...
                    </span>
                    <span v-else>Update Status</span>
                  </button>
                </div>
              </div>
              <!-- End Status Card -->

              <!-- Price Breakdown -->
              <div class="card mt-4">
                <div class="card-header">
                  <h2 class="h4 card-header-title">Price Breakdown</h2>
                </div>
                <div class="card-body">
                  <table class="table table-sm mb-0">
                    <tbody>
                      <tr>
                        <th>Currency</th>
                        <td>{{ row.currency }}</td>
                      </tr>
                      <tr>
                        <th>Rooms</th>
                        <td class="text-right">
                          {{ Number(row.subtotal_base).toFixed(2) }}
                        </td>
                      </tr>
                      <tr v-if="row.subtotal_room_adjustments != 0">
                        <th>Room Adj.</th>
                        <td class="text-right">
                          {{ Number(row.subtotal_room_adjustments).toFixed(2) }}
                        </td>
                      </tr>
                      <tr v-if="row.subtotal_children != 0">
                        <th>Children</th>
                        <td class="text-right">
                          {{ Number(row.subtotal_children).toFixed(2) }}
                        </td>
                      </tr>
                      <tr
                        v-for="(tier, i) in childBreakdownLines"
                        :key="'ctier_' + i"
                      >
                        <td
                          class="pl-4 text-muted"
                          style="font-size:.85em;padding-left:1.5rem;"
                        >
                          ↳ Age {{ tier.range }}
                        </td>
                        <td
                          class="text-right text-muted"
                          style="font-size:.85em;"
                        >
                          {{ tier.count }} ×
                          {{ Number(tier.unit).toFixed(2) }} =
                          {{ Number(tier.subtotal).toFixed(2) }}
                        </td>
                      </tr>
                      <tr v-if="row.subtotal_extras != 0">
                        <th>Extras</th>
                        <td class="text-right">
                          {{ Number(row.subtotal_extras).toFixed(2) }}
                        </td>
                      </tr>
                      <tr class="table-active">
                        <th>Total</th>
                        <td class="text-right font-weight-bold">
                          {{ row.currency }}
                          {{ Number(row.total_price).toFixed(2) }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <!-- End Price Breakdown -->
            </div>
            <!-- End Right Sidebar -->
          </div>
          <!-- End Row -->

          <!-- Back Button -->
          <div class="col-md-12 row mt-2" v-if="!pgLoading">
            <div class="form-group">
              <button
                type="button"
                class="btn btn-secondary"
                @click="$router.push({ name: 'bookings' })"
              >
                <span class="ti-arrow-left"></span> Back to Bookings
              </button>
            </div>
          </div>
        </div>

        <Footer></Footer>
      </div>
    </main>
  </div>
</template>

<script>
import Header from "../layouts/Header.vue";
import Navigation from "../layouts/Navigation";
import Footer from "../layouts/Footer.vue";
import iziToast from "izitoast";

export default {
  name: "BookingShow",
  components: { Header, Navigation, Footer },
  data() {
    return {
      auth: { role: "", access_token: "" },
      row: {},
      newStatus: "pending",
      pgLoading: true,
      btnLoading: false,
      refs: "bookings",
    };
  },
  created() {
    if (localStorage.getItem("role"))
      this.auth.role = localStorage.getItem("role");
    if (localStorage.getItem("access_token"))
      this.auth.access_token = localStorage.getItem("access_token");
    this.fetchRow();
  },
  computed: {
    locationLabel() {
      return [this.row.city, this.row.state, this.row.country]
        .filter(Boolean)
        .join(", ");
    },
    childrenSummary() {
      if (!this.row.children || !this.row.children.length) return "–";
      const c611 = this.row.children.filter((c) => c.age >= 6 && c.age <= 11)
        .length;
      const c25 = this.row.children.filter((c) => c.age >= 2 && c.age <= 5)
        .length;
      const inf = this.row.children.filter((c) => c.age < 2).length;
      const parts = [];
      if (c611)
        parts.push(c611 + " child" + (c611 > 1 ? "ren" : "") + " (6–11)");
      if (c25) parts.push(c25 + " child" + (c25 > 1 ? "ren" : "") + " (2–5)");
      if (inf) parts.push(inf + " infant" + (inf > 1 ? "s" : ""));
      return parts.join(", ") || "–";
    },
    childBreakdownLines() {
      if (!this.row.pricing_breakdown) return [];
      const tiers = {};
      this.row.pricing_breakdown.forEach((l) => {
        if (l.line !== "child_adjustment") return;
        const key = l.age >= 6 && l.age <= 11 ? "6–11" : "2–5";
        if (!tiers[key])
          tiers[key] = { count: 0, subtotal: 0, unit: l.unit_price };
        tiers[key].count++;
        tiers[key].subtotal += parseFloat(l.subtotal);
      });
      return Object.entries(tiers).map(([range, g]) => ({ range, ...g }));
    },
  },
  methods: {
    travelMonthLabel(row) {
      const month = parseInt(row.travel_month, 10);
      if (!month) return "–";
      const months = [
        "",
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      const year = row.travel_year ? " " + row.travel_year : "";
      return (months[month] || "Month " + month) + year;
    },

    fetchRow() {
      this.pgLoading = true;
      this.axios.defaults.headers.common = {
        "X-Requested-With": "XMLHttpRequest",
        Authorization: "Bearer " + this.auth.access_token,
      };
      this.axios({
        url: window.baseURL + "/" + this.refs + "/" + this.$route.params.id,
        method: "GET",
      })
        .then((res) => {
          this.pgLoading = false;
          this.row = res.data.row;
          this.newStatus = this.row.status;
        })
        .catch((err) => {
          this.pgLoading = false;
          if (err.response && err.response.status == 401) {
            this.removeLocalStorage();
            this.$router.push({ name: "login" });
          } else {
            iziToast.warning({
              icon: "ti-alert",
              title: "Error",
              message: err.response ? err.response.data.message : "" + err,
            });
          }
        });
    },

    saveStatus() {
      this.btnLoading = true;
      this.axios.defaults.headers.common = {
        "X-Requested-With": "XMLHttpRequest",
        Authorization: "Bearer " + this.auth.access_token,
      };
      this.axios({
        url:
          window.baseURL +
          "/" +
          this.refs +
          "/" +
          this.$route.params.id +
          "/status",
        method: "PUT",
        data: { status: this.newStatus },
      })
        .then(() => {
          this.btnLoading = false;
          this.row.status = this.newStatus;
          iziToast.success({
            icon: "ti-check",
            title: "Updated",
            message: "Booking status updated.",
          });
        })
        .catch((err) => {
          this.btnLoading = false;
          iziToast.warning({
            icon: "ti-alert",
            title: "Error",
            message: err.response ? err.response.data.message : "" + err,
          });
        });
    },

    onCopy() {
      iziToast.success({
        icon: "ti-check",
        title: "Copied,",
        message: "IP address copied to clipboard",
      });
    },

    statusBadge(status) {
      const map = {
        paid: "badge badge-success",
        pending: "badge badge-warning",
        cancelled: "badge badge-danger",
        refunded: "badge badge-secondary",
      };
      return map[status] || "badge badge-light";
    },

    removeLocalStorage() {
      [
        "access_token",
        "user_image",
        "user_name",
        "user_id",
        "role",
        "tenant_id",
      ].forEach((k) => localStorage.removeItem(k));
    },
  },
};
</script>

<style scoped></style>
